import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useCaptcha } from '@/hooks/useCaptcha';

import { isResponseError } from '@/utils/errorUtils';
import { noop } from '@/utils/functionalUtils';
import { removeRecord } from '@/utils/storageUtils';

import { FormikField } from '@/components/forms/formik/FormikField';
import { FormikRedux } from '@/components/forms/formik/FormikRedux';
import { FormikSubmit } from '@/components/forms/formik/FormikSubmit';
import { CaptchaContainer } from '@/shared/captcha/CaptchaContainer';
import { CaptchaPolicy } from '@/shared/captcha/CaptchaPolicy';
import { ErrorText } from '@/shared/errorText/ErrorText';
import { PasswordField } from '@/shared/passwordField/PasswordField';
import { TextField } from '@/shared/textField/TextField';

import type { TOnSubmit } from '@/types/formTypes';

import { signin } from '@/store/profile/profile.actions';
import { selectSignInError } from '@/store/profile/profile.selectors';
import { ActionEvents, trackEvent } from '@/tracking';
import { signinSchema } from '@/validation/signinSchema';

import { useStyles as useFormStyles } from '../AuthForms.styles';
import type { ISigninForm } from '../types';

import { useStyles } from './SignInForm.styles';

type TEmailSignInFormProps = {
  initialEmail: string | null;
  onEmailChanged?: (email: string) => void;
  emailDisabled?: boolean;
};

export const EmailSignInForm = ({
  initialEmail,
  onEmailChanged = noop,
  emailDisabled,
}: TEmailSignInFormProps) => {
  const signInError = useSelector(selectSignInError);
  const formClasses = useFormStyles();
  const signInClasses = useStyles();
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const { captcha, captchaContainerRef, captchaFailed } = useCaptcha('signin', signInError);

  const onSubmit: TOnSubmit<ISigninForm> = (values) => {
    const { email, password } = values;

    trackEvent(ActionEvents.loginStarted);
    dispatch(signin.request({ username: email.trim(), password, captcha }));

    removeRecord('justLoggedOutEmail');
  };

  const deriveErrors = (error: unknown): Record<string, string> => {
    if (isResponseError(error, 400, 'authentication')) {
      return { password: t('landing.welcome.badCredentials') };
    }

    return {};
  };

  return (
    <FormikRedux
      className={formClasses.form}
      storePath="profile.signin"
      deriveErrors={deriveErrors}
      initialValues={signinSchema.cast({ email: initialEmail })}
      validationSchema={signinSchema}
      onSubmit={onSubmit}
    >
      <FormikField
        name="email"
        type="email"
        label="landing.welcome.emailLabel"
        placeholder="landing.welcome.emailPlaceholder"
        onChange={(e) => {
          onEmailChanged(e.target.value);
        }}
        Component={TextField}
        disabled={emailDisabled}
      />

      <FormikField
        margin="normal"
        name="password"
        label="password.label"
        placeholder="password.enterPlaceholder"
        Component={PasswordField}
        ErrorComponent={(props) => (
          <div className={signInClasses.passwordNotes}>
            <ErrorText {...props} />
          </div>
        )}
      />

      {captcha && (
        <CaptchaContainer
          ref={captchaContainerRef}
          version={captcha.version}
          className={signInClasses.captchaContainer}
        />
      )}

      <FormikSubmit
        className={formClasses.continueButton}
        disabled={captchaFailed}
        data-testid="signin"
      >
        {t('landing.signin.signIn')}
      </FormikSubmit>

      {captcha && <CaptchaPolicy className={signInClasses.captchaPolicy} />}
    </FormikRedux>
  );
};
